import { apiSlice, TAG_TYPES } from "../api/apiSlice";
import { updateUserName } from "../../store/modules/userData";

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUserConnections: builder.query({
      query: (params) => {
        if (params && typeof params === "object") {
          const { userid, isMarketerProfile } = params;
          return `/conection/publicOrShopAgainstPublic/?businessType=generalPublic&userid=${userid}${isMarketerProfile ? "&isMarketerProfile=true" : ""}`;
        }
        return `/conection/publicOrShopAgainstPublic/?businessType=generalPublic&userid=${params}`;
      },
      providesTags: ["UserConnections"],
    }),
    getUserInfo: builder.query({
      query: (id) => `/user/id?id=${id}`,
      providesTags: (result, error, id) => [{ type: "UserInfo", id }],
    }),
    searchRegisteredUserByEmail: builder.query({
      query: (arg) => {
        if (typeof arg === "object" && arg !== null) {
          const { email, shopId } = arg;
          return {
            url: "/user/search-by-email",
            params: {
              email,
              ...(shopId ? { shopId } : {}),
            },
          };
        }
        return {
          url: "/user/search-by-email",
          params: { email: arg },
        };
      },
      keepUnusedDataFor: 0,
    }),
    getUserSuggestions: builder.query({
      query: (userId) => `/suggestion/getallbyuserId?userId=${userId}`,
      providesTags: ["UserSuggestions"],
    }),
    getReportShops: builder.query({
      query: (userId) => `/reportshop/getallbyuserid/?userId=${userId}`,
      providesTags: ["ReportShops"],
    }),
    getFaqs: builder.query({
      query: (faqType) => `/faq/?faqType=${faqType}`,
    }),
    getReportServices: builder.query({
      query: (userId) => `/reportservice/getallbyuserid/?userId=${userId}`,
      providesTags: ["ReportServices"],
    }),
    getUserServices: builder.query({
      query: (userId) => `/service/user/?userid=${userId}`,
      providesTags: ["UserServices"],
    }),
    getUserCompanies: builder.query({
      query: (params) => ({
        url: `/company`,
        params,
      }),
      providesTags: [TAG_TYPES.USER_COMPANIES],
    }),
    createCompany: builder.mutation({
      query: (data) => ({
        url: "/company",
        method: "POST",
        body: data,
      }),
      invalidatesTags: [TAG_TYPES.USER_COMPANIES],
      // Push new company into list cache immediately (no poll / websocket / refresh)
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: response } = await queryFulfilled;
          const company = response?.data;
          const userid = arg?.userid;
          if (!company?._id || !userid) return;

          const isJobsCompany =
            arg?.isJobsCompany === true ||
            arg?.isJobsCompany === "true" ||
            arg?.businessType === "jobsCompany";

          const listArgs = isJobsCompany
            ? { userid, isJobsCompany: true }
            : {
                userid,
                isBusinessProperty: true,
                isJobsCompany: false,
              };

          dispatch(
            apiSlice.util.updateQueryData(
              "getUserCompanies",
              listArgs,
              (draft) => {
                if (!draft?.data) return;
                if (!Array.isArray(draft.data.Companies)) {
                  draft.data.Companies = [];
                }
                const alreadyListed = draft.data.Companies.some(
                  (c) => String(c?._id) === String(company._id)
                );
                if (!alreadyListed) {
                  draft.data.Companies.unshift(company);
                }
              }
            )
          );
        } catch {
          // Create failed — leave cache unchanged
        }
      },
    }),
    getCompanyById: builder.query({
      query: (id) => `/company/id/?id=${id}`,
      providesTags: (result, error, id) => [
        { type: TAG_TYPES.USER_COMPANIES, id },
      ],
    }),
    searchCompanyByName: builder.query({
      query: ({ title, businessType }) =>
        `/company/searchbyname/?title=${title}&businessType=${businessType}`,
    }),
    searchPropertyCompanyByName: builder.query({
      query: (subCategory) =>
        `/company/searchbyname/?businessType=businessPropertyCompany&subCategory=${subCategory}`,
    }),
    getJobById: builder.query({
      query: (id) => `/job/id/?id=${id}`,
      providesTags: (result, error, id) => [{ type: "Jobs", id }],
    }),
    createJob: builder.mutation({
      query: (data) => ({
        url: "/job/",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Jobs"],
    }),
    updateJob: builder.mutation({
      query: ({ id, data }) => ({
        url: `/job/id/?id=${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "Jobs", id },
        "Jobs",
      ],
    }),
    getJobsFilter: builder.query({
      query: (params) => ({
        url: "/job/filter/",
        params,
      }),
      providesTags: ["Jobs"],
    }),
    getJobsByCompany: builder.query({
      query: (companyId) => `/job/company/?companyId=${companyId}`,
      providesTags: (result, error, companyId) => [
        { type: "Jobs", id: companyId || "LIST" },
      ],
    }),
    getTotalPersonOnCompany: builder.query({
      query: (companyid) =>
        `/jobapplied/totalPersonOnCompany?companyid=${companyid}`,
      providesTags: ["JobApplications"],
    }),
    deleteJob: builder.mutation({
      query: (id) => ({
        url: `/job/?id=${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [{ type: "Jobs", id }, "Jobs"],
    }),
    applyJob: builder.mutation({
      query: (data) => ({
        url: "/jobapplied/applyjob",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["JobApplications"],
    }),
    updateUserProfile: builder.mutation({
      query: ({ id, data }) => ({
        url: `/user/id/?id=${id}`,
        method: "PUT",
        body: data,
      }),
      // Optimistic update: immediately update `getUserInfo` cache for this id,
      // rollback on failure and finally ensure server response is applied.
      async onQueryStarted({ id, data }, { dispatch, queryFulfilled }) {
        let patchResult;
        try {
          patchResult = dispatch(
            apiSlice.util.updateQueryData("getUserInfo", id, (draft) => {
              // Draft shape: { data: { User: { ... } } }
              if (!draft) return;
              const user = draft.data?.User;
              if (user) {
                Object.keys(data).forEach((key) => {
                  // shallow merge optimistic fields
                  user[key] = data[key];
                });
              }
            }),
          );

          const { data: result } = await queryFulfilled;

          // apply server-confirmed response to cache
          if (result?.data?.User) {
            dispatch(
              apiSlice.util.updateQueryData("getUserInfo", id, (draft) => {
                if (!draft) return;
                draft.data.User = result.data.User;
              }),
            );

            // Also patch the visitGPProfile cache so /other-profile/:id pages
            dispatch(
              apiSlice.util.updateQueryData(
                "getVisitGPProfile",
                { userId: id, viewerId: id },
                (draft) => {
                  if (!draft) return;
                  const payload = draft?.data ?? draft;
                  if (payload?.User) {
                    Object.keys(result.data.User).forEach((key) => {
                      payload.User[key] = result.data.User[key];
                    });
                  }
                },
              ),
            );

            // instantly sync global Redux state (e.g. for Navbar images, global data)
            dispatch(updateUserName(result.data.User));
          }
        } catch (err) {
          // rollback optimistic update on failure
          if (patchResult) patchResult.undo();
        }
      },
      // Invalidate both UserInfo and VisitProfile caches so other-profile
      // pages pick up fresh images/cover the next time they mount or get focused
      invalidatesTags: (result, error, { id } = {}) => [
        "UserInfo",
        { type: "UserInfo", id: String(id) },
        TAG_TYPES.VISIT_PROFILE,
        { type: TAG_TYPES.VISIT_PROFILE, id: String(id) },
      ],
    }),
    getAllConnected: builder.query({
      query: (params) => {
        const queryParams = new URLSearchParams(params).toString();
        return `/conection/getAllConnected/?${queryParams}`;
      },
      providesTags: ["UserConnections"],
    }),
    checkMarketerFor: builder.query({
      query: (userid) => `/marketingagent/CheckMarketerFor?userid=${userid}`,
      providesTags: ["MarketerFor"],
    }),
    checkMarketerAvailability: builder.query({
      query: (userid) => `/marketingagent/availability?userid=${userid}`,
      providesTags: ["MarketerAvailability"],
    }),
    becomeMarketer: builder.mutation({
      query: ({ id, data }) => ({
        url: `/user/becomeMarketer?id=${id}`,
        method: "PATCH",
        body: data,
      }),
      async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          if (result?.success && result?.data?.user) {
            dispatch(updateUserName(result.data.user));
          }
        } catch (err) {
          console.debug("[becomeMarketer] onQueryStarted error:", err);
        }
      },
      invalidatesTags: (result, error, { id }) => [
        { type: "UserInfo", id },
        "Marketers",
        "Marketer",
        "MarketerAvailability",
      ],
    }),
    getAllowedZones: builder.query({
      query: () => `/marketingagent/allowed-zones`,
      providesTags: ["AllowedZones"],
    }),
    getCvForm: builder.query({
      query: (id) => `/cvform?id=${id}`,
      providesTags: (result, error, id) => [
        { type: TAG_TYPES.CV_FORM, id },
        TAG_TYPES.CV_FORM,
      ],
    }),
    getCvExist: builder.query({
      query: (userId) => `/cvform/checkcvexist?user=${userId}`,
      providesTags: (result, error, userId) => [
        { type: TAG_TYPES.CV_FORM, id: userId },
        TAG_TYPES.CV_FORM,
      ],
    }),
    postCvForm: builder.mutation({
      query: (body) => ({
        url: "/cvform/postNew",
        method: "POST",
        body,
      }),
      // Invalidate so CVTemplates refetches fresh data after CV is created
      invalidatesTags: (result, error, body) => [
        { type: TAG_TYPES.CV_FORM, id: body?.user },
        TAG_TYPES.CV_FORM,
      ],
    }),
    updateCvForm: builder.mutation({
      query: (body) => ({
        url: "/cvform/updatecv",
        method: "PUT",
        body,
      }),
      // Invalidate so CVTemplates refetches fresh data after CV is updated
      invalidatesTags: (result, error, body) => [
        { type: TAG_TYPES.CV_FORM, id: body?.user },
        TAG_TYPES.CV_FORM,
      ],
    }),
    updateCvTemplate: builder.mutation({
      query: ({ userId, templateIndex }) => ({
        url: `/cvform/update?user=${userId}&template=${templateIndex}`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, { userId }) => [
        { type: TAG_TYPES.CV_FORM, id: userId },
      ],
    }),
    getEmployeeByUserId: builder.query({
      query: (userId) => `/employee/userid?userid=${userId}`,
      providesTags: (result, error, userId) => [
        { type: TAG_TYPES.EMPLOYEE, id: userId },
        TAG_TYPES.EMPLOYEE,
      ],
    }),
    getEmployees: builder.query({
      query: () => `/employee/`,
    }),
    getFilteredEmployees: builder.query({
      query: (queryString) => `/employee/filter/?${queryString}`,
    }),
    createHelp: builder.mutation({
      query: (data) => ({
        url: "/help/",
        method: "POST",
        body: data,
      }),
    }),
    markWelcomeModalSeen: builder.mutation({
      query: (data) => ({
        url: "/user/welcome-modal-seen",
        method: "PATCH",
        body: data || {},
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data: result } = await queryFulfilled;
          if (result?.success) {
            dispatch((_, getState) => {
              const state = getState();
              const currentUser = state.user?.name;
              if (currentUser && typeof currentUser === "object") {
                dispatch(
                  updateUserName({
                    ...currentUser,
                    hasSeenWelcomeModal: true,
                  })
                );
              }
            });
          }
        } catch (err) {
          console.debug("[markWelcomeModalSeen] onQueryStarted error:", err);
        }
      },
    }),
  }),
});

export const {
  useGetUserConnectionsQuery,
  useGetUserInfoQuery,
  useLazyGetUserInfoQuery,
  useLazySearchRegisteredUserByEmailQuery,
  useUpdateUserProfileMutation,
  useGetCvExistQuery,
  useGetAllConnectedQuery,
  useCheckMarketerForQuery,
  useCheckMarketerAvailabilityQuery,
  useGetUserSuggestionsQuery,
  useGetReportShopsQuery,
  useGetReportServicesQuery,
  useGetUserServicesQuery,
  useGetUserCompaniesQuery,
  useLazyGetUserCompaniesQuery,
  useCreateCompanyMutation,
  useGetCompanyByIdQuery,
  useSearchCompanyByNameQuery,
  useLazySearchCompanyByNameQuery,
  useLazySearchPropertyCompanyByNameQuery,
  useGetJobByIdQuery,
  useCreateJobMutation,
  useUpdateJobMutation,
  useGetJobsFilterQuery,
  useLazyGetJobsFilterQuery,
  useGetJobsByCompanyQuery,
  useGetTotalPersonOnCompanyQuery,
  useDeleteJobMutation,
  useApplyJobMutation,
  useBecomeMarketerMutation,
  useGetAllowedZonesQuery,
  useGetCvFormQuery,
  usePostCvFormMutation,
  useUpdateCvFormMutation,
  useUpdateCvTemplateMutation,
  useLazyGetEmployeeByUserIdQuery,
  useGetEmployeesQuery,
  useLazyGetFilteredEmployeesQuery,
  useCreateHelpMutation,
  useGetFaqsQuery,
  useMarkWelcomeModalSeenMutation,
} = userApiSlice;

